<template>
  <div class="rg-object-detail-item" :style="cssVars">
    <div class="left">
      <div class="object-picture-container">
        <div class="loader" v-show="loading"></div>
        <div v-show="!loading">
          <div v-if="hasError" class="aisea-text-10-400-white">
            {{ errorMessage }}
          </div>
          <img
            v-if="hasSrc"
            v-show="!hasError"
            class="object-picture"
            :src="src"
            alt="object-picture"
            @load="imageLoaded"
            @error="imageLoadingError"
          />
          <span class="expand-button" v-if="canExpand" @click="expandPicture">
            <expand-icon></expand-icon>
          </span>
        </div>
      </div>
      <div class="footer">
        <div class="map-display">
          <div class="rg-object-icon">
            <slot name="rg-object-icon"></slot>
          </div>
          <div class="track-number">
            {{ trackNumber }}
          </div>
        </div>
        <div class="risk-assessment-result">
          <div class="risk-level">
            {{ riskLevelText }}
          </div>
          <div class="risk-assessment-type-indicator">
            {{ assessmentType }}
          </div>
        </div>
      </div>
    </div>
    <div class="center rg-object-info-container">
      <div class="confidence">
        <div class="label aisea-text-12-400-mouse-gray">物体種別</div>
        <div class="value aisea-text-14-400-white">
          {{ objectRecognitionRate }}
        </div>
      </div>
      <div class="sog">
        <div class="label aisea-text-12-400-mouse-gray">速度</div>
        <div class="value aisea-text-14-400-white">{{ sogForDisplay }}kn</div>
      </div>
      <div class="cog">
        <div class="label aisea-text-12-400-mouse-gray">針路</div>
        <div class="value aisea-text-14-400-white">{{ cogForDisplay }}°</div>
      </div>
      <div class="latitude">
        <div class="label aisea-text-12-400-mouse-gray">緯度</div>
        <div class="value aisea-text-14-400-white">
          {{ latitudeForDisplay }}
        </div>
      </div>
      <div class="longitude">
        <div class="label aisea-text-12-400-mouse-gray">経度</div>
        <div class="value aisea-text-14-400-white">
          {{ longitudeForDisplay }}
        </div>
      </div>
      <div class="shooting-date">
        <div class="label aisea-text-12-400-mouse-gray">撮影日時</div>
        <div class="value aisea-text-12-400-white">
          {{ shootingDate }}
        </div>
      </div>
      <div class="camera-name">
        <div class="label aisea-text-12-400-mouse-gray">カメラ</div>
        <div class="value aisea-text-12-400-white">
          {{ cameraName }}
        </div>
      </div>
    </div>
    <div class="right">
      <span class="deselect-button" @click="deselectItem">
        <cross-icon></cross-icon>
      </span>
    </div>
  </div>
</template>

<script>
import CrossIcon from "../../icons/CrossIcon.vue";
import ExpandIcon from "../../icons/ExpandIcon.vue";
import * as NumberUtil from "@/utils/NumberUtil";
import { GeoUtil } from "@/utils";
import isString from "lodash/isString";
import { RadarGridMixin } from "@/mixins";
const FALLBACK_COORDINATES_FOR_DISPLAY = "---";

export const EVENTS = {
  DESELECT_ITEM: "deselect-item",
  EXPAND_PICTURE: "expand-picture",
  IMAGE_LOADED: "image-loaded",
  IMAGE_LOADING_ERROR: "image-loading-error",
};

export default {
  name: "RgObjectDetailItem",

  components: {
    CrossIcon,
    ExpandIcon,
  },

  mixins: [RadarGridMixin],

  props: {
    loading: {
      type: Boolean,
      default: true,
    },
    src: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: "",
    },
    trackNumber: {
      type: [String, Number],
      default: "",
    },
    riskLevelText: {
      type: String,
      required: true,
    },
    textColor: {
      type: String,
      default: "#f2f61c",
    },
    assessmentType: {
      type: String,
      required: true,
    },
    k: {
      type: [String],
      default: "---",
    },
    confidence: {
      type: [String, Number],
      default: "---",
    },
    sog: {
      type: Number,
      default: 0,
    },
    cog: {
      type: Number,
      default: null,
    },
    longitude: {
      type: Number,
      required: false,
      default: null,
    },
    latitude: {
      type: Number,
      default: null,
    },
    shootingDate: {
      type: String,
      default: "",
    },
    cameraName: {
      type: String,
      default: "",
    },
  },

  computed: {
    canExpand() {
      const { hasSrc, hasError } = this;
      return hasSrc && !hasError;
    },
    hasError() {
      return this.errorMessage.length > 0;
    },
    hasSrc() {
      return isString(this.src) && this.src.length > 0;
    },
    cssVars() {
      const { textColor } = this;
      return {
        "--risk-level-text-color": textColor,
      };
    },
    objectRecognitionRate() {
      const { k, confidence } = this;
      return this.formatObjectRecognitionRate(k, confidence);
    },
    cogForDisplay() {
      const { cog } = this;
      if (isNaN(cog)) return "";
      return NumberUtil.toTrunc(cog, 1).toString();
    },
    sogForDisplay() {
      const { sog } = this;
      if (isNaN(sog)) return "";
      return NumberUtil.toTrunc(sog, 1).toString();
    },
    hasCoordinates() {
      return this.latitude && this.longitude;
    },
    hasValidLatitude() {
      const { latitude } = this;
      return GeoUtil.isValidLatitude(latitude);
    },
    hasValidLongitude() {
      const { longitude } = this;
      return GeoUtil.isValidLongitude(longitude);
    },
    latitudeForDisplay() {
      const { hasCoordinates, calcLatitudePrefix, latitude } = this;
      if (!hasCoordinates) return FALLBACK_COORDINATES_FOR_DISPLAY;
      const prefix = calcLatitudePrefix();
      const latitudeText = GeoUtil.toSexagesimalScale(Math.abs(latitude));
      return `${prefix}${latitudeText}`;
    },
    longitudeForDisplay() {
      const { hasCoordinates, calcLongitudePrefix, longitude } = this;
      if (!hasCoordinates) return FALLBACK_COORDINATES_FOR_DISPLAY;
      const prefix = calcLongitudePrefix();
      const longitudeText = GeoUtil.toSexagesimalScale(Math.abs(longitude));
      return `${prefix}${longitudeText}`;
    },
  },
  methods: {
    deselectItem() {
      this.$emit(EVENTS.DESELECT_ITEM);
    },
    expandPicture(id) {
      this.$emit(EVENTS.EXPAND_PICTURE, id);
    },
    imageLoadingError() {
      this.$emit(EVENTS.IMAGE_LOADING_ERROR);
    },
    imageLoaded() {
      this.$emit(EVENTS.IMAGE_LOADED);
    },
    calcLatitudePrefix() {
      if (!this.hasValidLatitude) return "";
      if (GeoUtil.isNorthLatitude(this.latitude)) return "N";
      return "S";
    },
    calcLongitudePrefix() {
      if (!this.hasValidLongitude) return "";
      if (GeoUtil.isEastLongitude(this.longitude)) return "E";
      return "W";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
.rg-object-detail-item {
  --risk-level-text-color: #f2f61c;
  @mixin button-paint-style {
    background-color: #222;
    border-radius: 50%;
    &:hover {
      background-color: #333;
    }
  }

  // 画像読み込み中を知らせるスタイル
  @mixin loading {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #fff;
    box-shadow: 16px 0 #fff, -16px 0 #fff;
    animation: flash 0.5s ease-out infinite alternate;
  }
  @keyframes flash {
    0% {
      background-color: #fff2;
      box-shadow: 16px 0 #fff2, -16px 0 #fff;
    }
    50% {
      background-color: #fff;
      box-shadow: 16px 0 #fff2, -16px 0 #fff2;
    }
    100% {
      background-color: #fff2;
      box-shadow: 16px 0 #fff, -16px 0 #fff2;
    }
  }

  padding: 10px 10px 4px 10px;
  display: grid;
  grid-template-columns: auto minmax(172px, auto) auto;
  gap: 10px;
  line-height: 0; // アイコンの高さなどがおかしくなるのを防ぐ

  border-radius: 5px;
  background-color: #021431;
  font-family: $aisea-font-family;
  user-select: none;
  .left {
    display: grid;
    grid-template-rows: 135px 1fr;
    row-gap: 4px;
    .object-picture-container {
      position: relative;
      width: 240px;
      height: 135px;
      display: flex;
      justify-content: center;
      align-items: center;

      border-radius: 5px;
      background-color: #222222;
      .loader {
        @include loading;
      }
      .object-picture {
        width: 100%;
        border-radius: 5px;

        object-fit: contain;
      }
      .expand-button {
        @include button-paint-style;

        position: absolute;
        bottom: 5px;
        left: 6px;

        cursor: pointer;
      }
    }
    .footer {
      display: grid;
      grid-template-columns: fit-content(100%) 1fr;
      .map-display {
        display: grid;
        grid-template-columns: 28px 1fr;
        align-items: center;
        gap: 4px;
        .rg-object-icon {
          width: 28px;
        }
        .track-number {
          font-size: 16px;
          font-weight: 700;
          color: var(--risk-level-text-color);
        }
      }
      .risk-assessment-result {
        display: grid;
        grid-template-columns: fit-content(100%) fit-content(100%);
        justify-items: end;
        align-items: center;
        margin-left: auto;

        color: var(--risk-level-text-color);
        font-size: 12px;
        font-weight: 600;
      }
    }
  }
  .center.rg-object-info-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 7px;

    line-height: 140%;
    .confidence,
    .sog,
    .cog,
    .latitude,
    .longitude,
    .shooting-date,
    .camera-name {
      display: grid;
      grid-template-columns: 50px 1fr;
      align-items: center;
      gap: 6px;
    }
  }
  .right {
    .deselect-button {
      @include button-paint-style;

      display: grid;
      grid-template-rows: fit-content(100%);

      cursor: pointer;
    }
  }
}
</style>
